/* eslint-disable react/display-name */
import React, { useMemo, useCallback, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Select from 'react-select';
import Tippy from '@tippy.js/react';
import { IconInfo } from 'svgIcons/MotionPortalIcons';
import {
    GeneralSelect,
    GeneralButton,
    SearchBox
} from 'sharedComponents';
import {
    getFormattedAssetType,
    customMultiSelectStyles,
    getSubscriptionListOptions,
    //Option,
    getSubsciptionTypeFilterValue,
    setOptions,
    compactMultiSelectStyles,
} from './helpers';
import './style.scss';
import Option from './Option';
import useUserCapability from 'sharedHooks/useUserCapability';
import { LoadingIndicator } from 'sharedComponents/PowertrainLoadingIndicator';
import FilterSubscriptionCustomValue from './FilterSubscriptionCustomValue';
import { LEFT_PANEL_WIDTH_PERCENT } from '../constants';

const FilterComponent = React.memo(({
    setAssetTypeFilter,
    assetTypeFilter,
    subscriptionTypes,
    setSubscriptionTypeFilter,
    subscriptionTypeFilter,
    resetFilters,
    isDisabled,
    isResetFilterDisabled,
    assetTypeFilterVisible,
    allAssets,
    leftPanelSize,
    setSensorIdentifierFilter,
    sensorIdentifierFilter,
}) => {
    const { t: translate } = useTranslation();
    const userCapabilities = useUserCapability();
    // Memoize the condition overview subscription filter capability
    const hasConditionOverviewSubscriptionFilter = useMemo(
        () => userCapabilities.hasConditionOverviewSubscriptionFilter(),
        [userCapabilities]
    );

    // Memoize the asset type options
    const assetTypeOptions = useMemo(() => setOptions(allAssets, translate), [translate, allAssets]);

    // Memoize the subscription type options
    const subscriptionOptions = useMemo(() => getSubscriptionListOptions(subscriptionTypes), [subscriptionTypes]);

    // Callback for resetting filters
    const handleResetFilters = useCallback(() => resetFilters(), [resetFilters]);

    return (
        <div className='powertrain-asset-overview-filters-container'>
            {/* Asset Type Filter Section */}
            {assetTypeFilterVisible &&
                <div className='filter-section asset-type-filter'>
                    <label htmlFor='asset-type-filter' className='section-title'>
                        {translate('ABB.Powertrain.Frontend.assetTypesLabel')}
                    </label>
                    <GeneralSelect
                        className='asset-type-filter'
                        inputId='asset-type-filter'
                        options={assetTypeOptions}
                        getOptionLabel={(option) => option.title}
                        placeholder={translate('ABB.Powertrain.Frontend.allassettypesLabel')}
                        getOptionValue={(option) => option.id}
                        value={getFormattedAssetType(assetTypeFilter, translate)}
                        menuShouldScrollIntoView={false}
                        onChange={value => setAssetTypeFilter(value?.assetType)}
                        isSearchable={false}
                        isDisabled={isDisabled}
                    />
                </div>
            }

            {/* Subscription Type Filter Section */}
            {hasConditionOverviewSubscriptionFilter &&
                <Suspense fallback={<LoadingIndicator />}>
                    <div className='filter-section subscription-type-filter'>
                        <label htmlFor='subscription-list' className='section-title'>
                            {translate('ABB.Powertrain.Frontend.subscriptionType')}
                        </label>
                        <div className='subscription-type'>
                            <Select
                                inputId='subscription-list'
                                styles={leftPanelSize >= LEFT_PANEL_WIDTH_PERCENT ? customMultiSelectStyles : compactMultiSelectStyles}
                                isMulti
                                name='subscription-list'
                                options={subscriptionOptions}
                                components={{ Option, MultiValueContainer: FilterSubscriptionCustomValue }}
                                hideSelectedOptions={false}
                                isSearchable={false}
                                isDisabled={isDisabled}
                                onChange={(selected) => setSubscriptionTypeFilter(selected)}
                                value={getSubsciptionTypeFilterValue(subscriptionTypes, subscriptionTypeFilter)}
                            />
                        </div>
                    </div>
                </Suspense>
            }

            <div className='filter-section sensor-identifier-filter'>
                <div className='sensor-identifier-header'>
                    <label htmlFor='sensor-identifier-filter' className='section-title'>
                        {translate('ABB.Powertrain.Frontend.dyn_lbl_SmartSensorIdentifier')}
                    </label>
                    <Tippy
                        content={translate('ABB.Powertrain.Frontend.sensorIdentifierFilterInfoTooltip')}
                        className='asset-overview-tooltip'
                        interactive={false}
                        animation='scale'
                        theme='light-border'
                        trigger='click'
                        placement='top'
                        maxWidth='400px'
                        boundary='viewport'
                        appendTo={document.body}
                    >
                        <button
                            type='button'
                            className='info-icon'
                            onClick={e => e.stopPropagation()}
                            aria-label='sensor-identifier-info'
                        >
                            <IconInfo width={'16'} height={'16'} color={'black'} />
                        </button>
                    </Tippy>
                </div>
                <SearchBox
                    className='sensor-identifier-filter'
                    placeholder={translate('ABB.Powertrain.Frontend.sensorIdentifierFilterPlaceholder')}
                    onChange={setSensorIdentifierFilter}
                    value={sensorIdentifierFilter}
                    maxLength={50}
                />
            </div>

            {/* Reset Filters Section */}
            <div className='filter-section reset-filters'>
                <GeneralButton
                    type='normal'
                    disabled={isResetFilterDisabled}
                    text={translate('ABB.Powertrain.Frontend.resetFiltersButton')}
                    onClick={handleResetFilters}
                />
            </div>
        </div>
    );
});

FilterComponent.propTypes = {
    setAssetTypeFilter: PropTypes.func.isRequired,
    assetTypeFilter: PropTypes.string,
    subscriptionTypes: PropTypes.array,
    setSubscriptionTypeFilter: PropTypes.func.isRequired,
    subscriptionTypeFilter: PropTypes.array.isRequired,
    resetFilters: PropTypes.func.isRequired,
    isDisabled: PropTypes.bool,
    isResetFilterDisabled: PropTypes.bool,
    assetTypeFilterVisible: PropTypes.bool,
    allAssets: PropTypes.array.isRequired,
    setSensorIdentifierFilter: PropTypes.func.isRequired,
    sensorIdentifierFilter: PropTypes.string
};

export default FilterComponent;
